import { i18n } from '~/libs/i18n'

function translate(key: string): string {
  return (i18n.global as unknown as { t: (k: string) => string }).t(key)
}

interface AxiosLikeError {
  code?: string
  message?: string
  response?: {
    status?: number
    data?: unknown
  }
}

/** Prefer `message` from JSON objects; also parse JSON strings (responseType: 'text'). */
function extractResponseDataMessage(data: unknown): unknown {
  if (data == null)
    return undefined

  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (!trimmed.startsWith('{') && !trimmed.startsWith('['))
      return undefined
    try {
      const parsed = JSON.parse(trimmed) as { message?: unknown }
      if (parsed && typeof parsed === 'object' && 'message' in parsed)
        return parsed.message
    }
    catch {
      return undefined
    }
    return undefined
  }

  if (typeof data === 'object' && data !== null && 'message' in data)
    return (data as { message?: unknown }).message

  return undefined
}

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return typeof error === 'object' && error !== null && ('response' in error || 'code' in error)
}

function resolveTransportError(error: AxiosLikeError): string | null {
  const status = error.response?.status
  if (status === 404)
    return translate('ADMIN_ERRO_ENDPOINT_NAO_ENCONTRADO')

  if (!error.response) {
    const code = error.code ?? ''
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || /network error/i.test(error.message ?? ''))
      return translate('ADMIN_ERRO_REDE')
  }

  return null
}

function decodeMaybeBase64(encoded: string): string {
  try {
    const binary = atob(encoded)
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  }
  catch {
    return encoded
  }
}

function looksLikeBase64(value: string): boolean {
  if (value.length < 8 || value.length % 4 !== 0)
    return false
  if (/\s/.test(value) || /[^\w+/=-]/.test(value))
    return false
  return true
}

function normalizeMessageText(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed)
    return ''

  if (trimmed === 'VOCE_NAO_TEM_NIVEL_ADMIN')
    return translate('VOCE_NAO_TEM_NIVEL_ADMIN')

  if (looksLikeBase64(trimmed)) {
    const decoded = decodeMaybeBase64(trimmed)
    if (decoded && decoded !== trimmed)
      return decoded
  }

  return trimmed
}

/**
 * Prefer API/axios denial text (e.g. insufficient admin level) over generic fallbacks.
 */
export function resolveAdminErrorMessage(
  error: unknown,
  fallbackKey = 'ERRO_GENERICO',
): string {
  const fallback = translate(fallbackKey)

  if (error == null || error === '')
    return fallback

  if (typeof error === 'string') {
    return normalizeMessageText(error) || fallback
  }

  if (typeof error === 'object') {
    const record = error as {
      message?: unknown
      response?: { status?: number, data?: unknown }
      code?: string
    }

    const transportMessage = isAxiosLikeError(error) ? resolveTransportError(error) : null
    if (transportMessage)
      return transportMessage

    const raw = extractResponseDataMessage(record.response?.data) ?? record.message
    if (Array.isArray(raw)) {
      const joined = raw.map(part => String(part)).join('<br/>')
      return normalizeMessageText(joined) || fallback
    }
    if (raw != null && String(raw).trim())
      return normalizeMessageText(String(raw)) || fallback
  }

  return fallback
}

export function adminResponseMessage(
  message: string | undefined | null,
  fallbackKey = 'ERRO_GENERICO',
): string {
  if (!message || !String(message).trim())
    return translate(fallbackKey)
  return normalizeMessageText(String(message)) || translate(fallbackKey)
}
